/**
 * The AI choices both modes share (merged page, GĐ2): the outfit picker (who it is for — Nam / Nữ / … — then the
 * outfit) and the premium "Model" with its provider consent. "Ảnh thẻ" uses them in "Chỉnh sửa nâng cao"
 * (AiFields), "Ảnh chuyên nghiệp" in its "AI" panel (editor/panels/AiPanel.tsx).
 */
import { useId, type Ref } from "react";
import type { PremiumModel } from "../api";
import { PREMIUM } from "../messages";

export interface OutfitGroup {
  key: string;
  label: string;
  outfits: string[];
}

interface OutfitProps {
  groups: OutfitGroup[];
  group: string;
  onGroup: (key: string) => void;
  /** the outfit chosen ("keep" = none) */
  value: string;
  onChange: (key: string) => void;
  labelOf: (key: string) => string;
  /** options after the group's outfits (the ID-photo mode's "Tự mô tả…") */
  extra?: string[];
  groupLegend: string;
  outfitLabel: string;
  /** the outfit select's label is said by a legend around the picker already */
  hideOutfitLabel?: boolean;
}

export function OutfitPicker({ groups, group, onGroup, value, onChange, labelOf, extra = [], groupLegend, outfitLabel,
                               hideOutfitLabel = false }: OutfitProps) {
  const ids = { group: useId(), outfit: useId() };
  const shown = groups.find((g) => g.key === group)?.outfits ?? [];
  const options = ["keep", ...shown, ...extra];
  return (
    <>
      <fieldset className="field">
        <legend id={ids.group}>{groupLegend}</legend>
        <div className="segmented" role="radiogroup" aria-labelledby={ids.group}>
          {groups.map((g) => (
            <label key={g.key} className={`segment${group === g.key ? " is-selected" : ""}`}>
              <input type="radio" name={ids.group} value={g.key} checked={group === g.key} onChange={() => onGroup(g.key)} />
              {g.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="field">
        <label htmlFor={ids.outfit} className={hideOutfitLabel ? "visually-hidden" : undefined}>{outfitLabel}</label>
        <select id={ids.outfit} value={options.includes(value) ? value : "keep"} onChange={(e) => onChange(e.target.value)}>
          {options.map((k) => <option key={k} value={k}>{labelOf(k)}</option>)}
        </select>
      </div>
    </>
  );
}

interface ModelProps {
  models: PremiumModel[];
  /** the chosen model's key, "" = the standard AI edit */
  value: string;
  onChange: (key: string) => void;
  standardCost: number;
  /** the member agreed to send the photo to the chosen model's provider */
  consent: boolean;
  onConsent?: (given: boolean) => void;
  consentRef?: Ref<HTMLInputElement>;
  /** said under the choice while the standard edit is chosen */
  idleHint?: string;
}

export function PremiumModelField({ models, value, onChange, standardCost, consent, onConsent, consentRef, idleHint }: ModelProps) {
  const ids = { model: useId(), hint: useId(), consent: useId() };
  const model = models.find((m) => m.key === value);
  const hint = model ? `${model.description} ${PREMIUM.topupOnly}` : idleHint;
  return (
    <div className="field premium-model">
      <label htmlFor={ids.model}>Model</label>
      <select id={ids.model} value={model?.key ?? ""} aria-describedby={hint ? ids.hint : undefined}
              onChange={(e) => onChange(e.target.value)}>
        <option value="">{PREMIUM.standard(standardCost)}</option>
        {models.map((m) => <option key={m.key} value={m.key} disabled={!m.available}>{PREMIUM.option(m)}</option>)}
      </select>
      {hint && <p id={ids.hint} className="hint">{hint}</p>}
      {model && onConsent && (
        <div className="check consent-provider">
          <input id={ids.consent} ref={consentRef} type="checkbox" checked={consent}
                 onChange={(e) => onConsent(e.target.checked)} />
          <label htmlFor={ids.consent}>{PREMIUM.consent(model.provider_label)}</label>
        </div>
      )}
    </div>
  );
}
